import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuCircleCheck } from 'react-icons/lu';

import { useRefreshRecordedDocuments } from '../useRefreshRecordedDocuments';
import { APICommunicator } from '~/api/APICommunicator';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useCallbackWithErrorHandler from '~/hooks/useCallbackWithErrorHandler';
import { setQueryPosition } from '~/store/queryPositionStore';
import { queryKeys } from '~/utils/queryKeys';

type Props = {
    confirmed: boolean;
    documentKey: string;
    leadId: string;
};

export const ConfirmationCell: React.FC<Props> = ({ confirmed, documentKey, leadId }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const refreshRecordedDocuments = useRefreshRecordedDocuments(leadId);
    const [isPending, setIsPending] = useState(false);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const isDisabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].changeDocumentAcknowledgment || isPending;

    const onChange = useCallbackWithErrorHandler(
        async (accepted: boolean) => {
            if (accepted === confirmed) return;

            setIsPending(true);
            try {
                const { position } = await APICommunicator.sendCommand({
                    aggregateId: leadId,
                    aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                    commandName: LOAN_REQUEST_COMMANDS.changeDocumentAcknowledgment,
                    payload: { accepted, key: documentKey },
                });

                // Confirming changes the upload eligibility, so this list must catch up too.
                const queryKeyUnverifiedDocuments = queryKeys.getLoanRequestsWithUnverifiedDocuments();
                setQueryPosition(JSON.stringify(queryKeyUnverifiedDocuments), position);
                // refreshRecordedDocuments also invalidates the acknowledged documents query.
                setQueryPosition(JSON.stringify(queryKeys.getAcknowledgedDocuments(leadId)), position);
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: queryKeyUnverifiedDocuments }),
                    refreshRecordedDocuments(position),
                ]);
            } finally {
                setIsPending(false);
            }
        },
        [confirmed, documentKey, leadId, queryClient, refreshRecordedDocuments],
    );

    if (confirmed)
        return (
            <button
                className="text-sm text-midnight disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isDisabled}
                onClick={() => onChange(false)}
                type="button"
            >
                {t('DOCUMENT_DEMANDER.SUMMARY.CONFIRMATION.CONFIRMED')}
            </button>
        );

    return (
        <Button
            variant={ButtonVariant.SECONDARY}
            iconLeft={<LuCircleCheck size={14} />}
            disabled={isDisabled}
            onClick={() => onChange(true)}
        >
            {t('DOCUMENT_DEMANDER.SUMMARY.CONFIRMATION.CONFIRM')}
        </Button>
    );
};
